/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/BannerFormModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useRef, useEffect, useState } from "react";
import styles from "./BannerFormModal.module.css";
import { useTranslation } from "react-i18next";
import FileUploadArea from "../ProposalCreateModal/FileUploadArea";
import { CircularProgress } from "@mui/material";
import { createFileObjectFromUrl } from "../../utils/common";
import { isAlreadyPresignedUrl } from "../../utils/assetUrl";
import { ReactComponent as WebsiteIcon } from "../../assets/icons/BannerFormModal/WebsiteIcon.svg";
import { createBanner, updateBanner } from "../../logic/banner";
import { useNavigate } from "react-router";

const BannerFormModal = ({
  open,
  onClose,
  banner = null,
  editMode = false,
  onBannerCreated,
  onBannerUpdated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bannerImage, setBannerImage] = useState(null);
  const [isNewBannerImageUploaded, setIsNewBannerImageUploaded] =
    useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");

  useEffect(() => {
    logger.info("banner:", { data: banner });
  }, [banner]);

  useEffect(() => {
    if (banner?.imageUrl) {
      setBannerImage(createFileObjectFromUrl(banner.imageUrl));
    } else {
      setBannerImage(null);
    }
  }, [banner?.imageUrl]);

  useEffect(() => {
    setRedirectUrl(banner?.redirectUrl || "");
  }, [banner?.redirectUrl]);

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open && dialog) {
      try {
        dialog.showModal();
      } catch (error) {
        logger.error("Error showing modal:", { err: sanitizeError(error) });
      }
    } else if (!open && dialog) {
      try {
        dialog.close();
      } catch (error) {
        logger.error("Error closing modal:", { err: sanitizeError(error) });
      }
    } else {
      // Dialog already matches the desired open state
    }

    return () => {
      if (dialog) {
        try {
          dialog.removeEventListener("close", handleClose);
        } catch (error) {
          logger.error("Error removing event listener:", {
            err: sanitizeError(error),
          });
        }
      }
    };
  }, [open, onClose]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileValidation(file);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await handleFileValidation(file);
    }
  };

  const handleFileValidation = async (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      setError(t("pages.buyerCompanyForm.logoFileTypeError"));
      setBannerImage(null);
      return;
    }

    setError("");
    setBannerImage(file);
    setIsNewBannerImageUploaded(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let result;
      const bannerData = {
        redirectUrl: redirectUrl,
        isNewBannerImageUploaded: isNewBannerImageUploaded,
      };

      if (editMode) {
        logger.info("is in edit mode");
        if (isNewBannerImageUploaded) {
          bannerData.bannerImageFileName = bannerImage?.name;
          logger.info("bannerData after setting bannerImageFileName:", {
            data: bannerData,
          });
        }

        result = await updateBanner(banner._id, bannerData, navigate);
      } else {
        bannerData.bannerImageFileName = bannerImage?.name;

        result = await createBanner(bannerData, navigate);
      }

      if (result && result.status !== "error") {
        const uploadUrl =
          (typeof result.presignedUrl === "string" && result.presignedUrl) ||
          result.presignedUrl?.uploadUrl ||
          "";
        const shouldUploadImage =
          bannerImage instanceof Blob &&
          (isNewBannerImageUploaded || !editMode);

        if (shouldUploadImage) {
          if (!isAlreadyPresignedUrl(uploadUrl)) {
            setError(t("errors.loading.bannerUploadFailed"));
            return;
          }

          try {
            const uploadResponse = await fetch(uploadUrl, {
              method: "PUT",
              body: bannerImage,
              headers: {
                "Content-Type": bannerImage.type || "application/octet-stream",
              },
            });

            if (!uploadResponse.ok) {
              throw new Error(
                `Failed to upload file: ${uploadResponse.statusText}`,
              );
            }
          } catch (error) {
            logger.error("Error uploading file:", {
              err: sanitizeError(error),
            });
            setError(t("errors.loading.bannerUploadFailed"));
            return;
          }
        }

        if (editMode && onBannerUpdated) {
          onBannerUpdated(bannerData);
        } else if (!editMode && onBannerCreated) {
          onBannerCreated(bannerData);
        } else {
          // No callback registered for this save mode
        }

        onClose();
      } else {
        logger.error("Failed to save banner");
        setError(t("errors.loading.bannerSaveFailed"));
      }
    } catch (error) {
      logger.error("Error saving banner:", { err: sanitizeError(error) });
      setError(t("errors.loading.bannerSaveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (editMode) {
      const hasImageChanges = isNewBannerImageUploaded && bannerImage !== null;
      const hasUrlChanges = redirectUrl !== (banner?.redirectUrl || "");

      return hasImageChanges || hasUrlChanges;
    } else {
      return bannerImage !== null;
    }
  };

  const getModalTitle = () => {
    return editMode
      ? t("components.bannerFormModal.editTitle")
      : t("components.bannerFormModal.createTitle");
  };

  const getSubmitButtonText = () => {
    if (isSubmitting) {
      return editMode
        ? t("components.bannerFormModal.saving")
        : t("components.bannerFormModal.creating");
    }
    return editMode
      ? t("components.bannerFormModal.saveButton")
      : t("components.bannerFormModal.createButton");
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={styles.modalOverlay}>
        <dialog ref={dialogRef} className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>{getModalTitle()}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.modalContent}>
            <form onSubmit={handleSubmit} className={styles.bannerForm}>
              <div className={styles.formSection}>
                <h3>{t("components.bannerFormModal.imageLabel")}</h3>
                <FileUploadArea
                  file={bannerImage}
                  isUploading={isUploading}
                  isDragging={isDragging}
                  error={error}
                  onFileChange={handleFileChange}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() =>
                    document.getElementById("bannerImageInput").click()
                  }
                />
                <input
                  id="bannerImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
              </div>

              <div className={styles.formSection}>
                <h3>{t("components.bannerFormModal.imageUrlLabel")}</h3>
                <div className={styles.inputWrapper}>
                  <WebsiteIcon className={styles.inputIcon} />
                  <input
                    type="url"
                    name="redirectUrl"
                    value={redirectUrl}
                    onChange={(e) => setRedirectUrl(e.target.value)}
                    className={styles.input}
                    placeholder={t(
                      "components.bannerFormModal.imageUrlPlaceholder",
                    )}
                  />
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t("components.bannerFormModal.cancelButton")}
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" />
                      <span>{getSubmitButtonText()}</span>
                    </>
                  ) : (
                    getSubmitButtonText()
                  )}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </div>
    </>
  );
};

export default BannerFormModal;
